import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Swal from '../librarianSwal';
import { useAuth } from '../../Constant/Auth';
import { useActiveLibraryId } from '../../Constant/useActiveLibrary';
import SearchableSelect from '../../shared/SearchableSelect';

import {
  getPaymentsByLibrary,
  searchMembersByLibrary,
  getMembershipDepositStatus,
  getPendingFinesByMemberAndLibrary,
  getPendingChargesByMemberAndLibrary,
  addPayment,
  addMemberCharge,
  initiateMembershipPayment,
  initiateFinePayment,
  initiateChargePayment,
  refundPayment,
  getErrorMessage
} from '../../api/api';
import { readPrefetched } from '../../api/prefetchCache';
import { exportToCSV } from '../exportUtils';
import '../lib-page-header.css';
import '../lib-typography.css';
import './PaymentList.css';
import Pagination from '../components/Pagination';

const PAYMENTS_RESOURCE = 'payments';
const PAGE_SIZE = 10;

const MEMBER_SEARCH_RESULT_LIMIT = 30;

const memberDisplayName = (member) =>
  member.full_name || member.member_name || member.parent_name || 'Member';

const memberLabel = (member) => {
  const name = memberDisplayName(member);
  return member.mobile ? `${name} - ${member.mobile}` : name;
};

// 'school_parent' -> 'School Parent', 'pending_payment' -> 'Pending Payment'.
const formatEnumLabel = (value) =>
  String(value || '')
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const MEMBERSHIP_STATUS_BADGE_CLASS = {
  active: 'bg-success',
  pending_payment: 'bg-warning text-dark',
  suspended: 'bg-danger',
  expired: 'bg-secondary'
};

const PAYMENT_FOR_OPTIONS = [
  { value: 'fine', label: 'Fine' },
  { value: 'membership_deposit', label: 'Membership Deposit' },
  { value: 'other', label: 'Other' }
];

const PAYMENT_MODE_OPTIONS = [
  { value: 'cash', label: 'Cash' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'hdfc', label: 'HDFC Online (Card / UPI / NetBanking)' }
];

const INITIAL_FORM = {
  member_id: '',
  membership_id: '',
  fine_id: '',
  charge_id: '',
  payment_for: 'fine',
  amount: '',
  payment_mode: 'cash',
  transaction_ref: '',
  remarks: '',
  new_charge_category: '',
  new_charge_description: '',
  new_charge_amount: ''
};

const PaymentList = ({ embedded = false, actionsPortalNode = null } = {}) => {
  useAuth();
  const libraryId = useActiveLibraryId();

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Loaded on demand once a member is picked in Create Payment — never
  // prefetched for every member in the library up front.
  const [memberFines, setMemberFines] = useState([]);
  const [memberCharges, setMemberCharges] = useState([]);
  const [loadingMemberData, setLoadingMemberData] = useState(false);
  const [creatingNewCharge, setCreatingNewCharge] = useState(false);

  // Member search combobox (name / mobile / membership no.) — queries the
  // backend directly via SearchableSelect's built-in remote mode (it already
  // debounces keystrokes 300ms internally) rather than filtering a
  // whole-library list preloaded up front, so results stay restricted to
  // this staff member's library and scale to large member lists.
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [memberSearchResults, setMemberSearchResults] = useState([]);
  const [memberSearchLoading, setMemberSearchLoading] = useState(false);
  const [memberSearchError, setMemberSearchError] = useState('');
  // Guards against a slow, now-stale search response overwriting the
  // results of a more recent keystroke's request.
  const memberSearchTermRef = useRef('');

  // The selected member's latest membership + server-calculated deposit due
  // (Payment For = Membership Deposit only) — fetched fresh per member, never
  // guessed or computed client-side.
  const [depositStatus, setDepositStatus] = useState(null);
  const [depositStatusFetched, setDepositStatusFetched] = useState(false);
  const [depositStatusLoading, setDepositStatusLoading] = useState(false);
  const [depositStatusError, setDepositStatusError] = useState('');

  const [formData, setFormData] = useState(INITIAL_FORM);

  const fetchData = async () => {
    try {
      if (!libraryId) return;

      setLoading(true);
      const paymentData = await getPaymentsByLibrary(libraryId);

      setPayments(paymentData || []);
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Seed the table instantly from a hover-triggered sidebar prefetch if
    // one is already warm, then always still run the real fetch below —
    // payment figures are never trusted from a cache older than this.
    const warmPayments = readPrefetched(PAYMENTS_RESOURCE, libraryId);
    if (warmPayments) setPayments(warmPayments || []);

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryId]);

  // Fires only once a member is actually selected in the modal — the fine
  // and charge pickers are populated on demand rather than pulling every
  // fine/charge in the library up front.
  useEffect(() => {
    let cancelled = false;

    const loadMemberScopedData = async () => {
      if (!formData.member_id || !libraryId) {
        setMemberFines([]);
        setMemberCharges([]);
        return;
      }

      try {
        setLoadingMemberData(true);
        const [pendingFines, pendingCharges] = await Promise.all([
          getPendingFinesByMemberAndLibrary(libraryId, formData.member_id),
          getPendingChargesByMemberAndLibrary(libraryId, formData.member_id)
        ]);

        if (cancelled) return;
        setMemberFines(pendingFines || []);
        setMemberCharges(pendingCharges || []);
      } catch (error) {
        if (!cancelled) Swal.fire('Error', getErrorMessage(error), 'error');
      } finally {
        if (!cancelled) setLoadingMemberData(false);
      }
    };

    loadMemberScopedData();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.member_id, libraryId]);

  // Fires only when Payment For = Membership Deposit and a member is
  // selected — fetches that member's latest membership + server-calculated
  // due amount, and auto-selects it into the form when it's actually
  // payable (pending_payment with a genuine due). Any other outcome
  // (active, fully paid, no membership at all) clears membership_id/amount
  // so Save can never be submitted against it.
  useEffect(() => {
    let cancelled = false;

    const loadDepositStatus = async () => {
      if (formData.payment_for !== 'membership_deposit' || !formData.member_id || !libraryId) {
        setDepositStatus(null);
        setDepositStatusFetched(false);
        setDepositStatusError('');
        return;
      }

      try {
        setDepositStatusLoading(true);
        setDepositStatusError('');
        const data = await getMembershipDepositStatus(libraryId, formData.member_id);

        if (cancelled) return;

        const membership = data?.membership || null;
        setDepositStatus(membership);
        setDepositStatusFetched(true);

        const payable = membership && membership.status === 'pending_payment' && Number(membership.due_amount) > 0;

        setFormData((prev) => ({
          ...prev,
          membership_id: payable ? String(membership.id) : '',
          amount: payable ? membership.due_amount : ''
        }));
      } catch (error) {
        if (!cancelled) {
          setDepositStatus(null);
          setDepositStatusFetched(true);
          setDepositStatusError(getErrorMessage(error));
        }
      } finally {
        if (!cancelled) setDepositStatusLoading(false);
      }
    };

    loadDepositStatus();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.payment_for, formData.member_id, libraryId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM);
    setCreatingNewCharge(false);
    setMemberFines([]);
    setMemberCharges([]);
    setMemberSearchTerm('');
    memberSearchTermRef.current = '';
    setMemberSearchResults([]);
    setMemberSearchLoading(false);
    setMemberSearchError('');
    setDepositStatus(null);
    setDepositStatusFetched(false);
    setDepositStatusError('');
  };

  const openModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    resetForm();
  };

  // Every field that depends on which member/purpose is selected — cleared
  // any time either one changes, so a stale membership/fine/charge/amount
  // from the previous selection can never be submitted.
  const clearDependentFields = (prev) => ({
    ...prev,
    membership_id: '',
    fine_id: '',
    charge_id: '',
    amount: ''
  });

  const clearDepositMessages = () => {
    setDepositStatus(null);
    setDepositStatusFetched(false);
    setDepositStatusError('');
  };

  const handleMemberSelect = (member) => {
    clearDepositMessages();
    setFormData((prev) => ({ ...clearDependentFields(prev), member_id: String(member.id) }));
  };

  const handleMemberClear = () => {
    setMemberSearchTerm('');
    memberSearchTermRef.current = '';
    setMemberSearchResults([]);
    setMemberSearchError('');
    clearDepositMessages();
    setFormData((prev) => ({ ...clearDependentFields(prev), member_id: '' }));
  };

  // SearchableSelect's onChange gives back the selected member's id
  // (e.target.value); look the full member object up from the last-fetched
  // search results so handleMemberSelect can still run unchanged. An empty
  // value means the "x" clear affordance was used.
  const handleMemberSearchableChange = (e) => {
    const memberId = e.target.value;

    if (!memberId) {
      handleMemberClear();
      return;
    }

    const member = memberSearchResults.find((m) => String(m.id) === String(memberId));
    if (member) handleMemberSelect(member);
  };

  // Wired to SearchableSelect's onSearchChange — it already debounces
  // keystrokes 300ms internally (and calls this once immediately on
  // focus/click too, with an empty term, so the field shows eligible
  // members before the librarian has typed anything). This always calls the
  // library-scoped search directly — an empty term still hits the backend,
  // which returns a small bounded preview (first ~20 members) rather than
  // the whole library, so opening the field never downloads the full member
  // list. A ref-tracked "latest requested term" guards against a slow,
  // now-stale response overwriting a more recent keystroke's results.
  const handleMemberSearchChange = async (term) => {
    const trimmed = term.trim();
    setMemberSearchTerm(trimmed);
    memberSearchTermRef.current = trimmed;

    if (!libraryId) {
      setMemberSearchResults([]);
      setMemberSearchError('');
      setMemberSearchLoading(false);
      return;
    }

    setMemberSearchLoading(true);
    setMemberSearchError('');

    try {
      const results = await searchMembersByLibrary(libraryId, trimmed);
      if (memberSearchTermRef.current !== trimmed) return;
      setMemberSearchResults((results || []).slice(0, MEMBER_SEARCH_RESULT_LIMIT));
    } catch (error) {
      if (memberSearchTermRef.current !== trimmed) return;
      setMemberSearchResults([]);
      setMemberSearchError(getErrorMessage(error));
    } finally {
      if (memberSearchTermRef.current === trimmed) setMemberSearchLoading(false);
    }
  };

  const handlePaymentForChange = (e) => {
    const paymentFor = e.target.value;

    setCreatingNewCharge(false);
    clearDepositMessages();
    setFormData((prev) => ({ ...clearDependentFields(prev), payment_for: paymentFor }));
  };

  const handleFineChange = (e) => {
    const fineId = e.target.value;
    const fine = memberFines.find((f) => String(f.id) === String(fineId));

    setFormData((prev) => ({
      ...prev,
      fine_id: fineId,
      // Outstanding amount is always calculated from the database and the
      // field is always locked for fines — never a manually typed amount.
      amount: fine ? Number(fine.balance_amount ?? fine.amount ?? 0) : ''
    }));
  };

  const handleChargeChange = (e) => {
    const chargeId = e.target.value;

    if (chargeId === '__new__') {
      setCreatingNewCharge(true);
      setFormData((prev) => ({ ...prev, charge_id: '', amount: '' }));
      return;
    }

    setCreatingNewCharge(false);
    const charge = memberCharges.find((c) => String(c.id) === String(chargeId));

    setFormData((prev) => ({
      ...prev,
      charge_id: chargeId,
      // "Other" charges are paid in full against their approved amount —
      // never an arbitrary frontend-entered figure.
      amount: charge ? Number(charge.amount || 0) : ''
    }));
  };

  const handleNewChargeAmountChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, new_charge_amount: value, amount: value }));
  };

  const handlePaymentModeChange = (e) => {
    setFormData((prev) => ({ ...prev, payment_mode: e.target.value }));
  };

  // Fine, membership deposit and approved "other" charge amounts are always
  // database-calculated and read-only, regardless of payment mode — only a
  // brand-new "other" charge being created inline still takes a typed amount.
  const isAmountLocked =
    formData.payment_for === 'fine' ||
    formData.payment_for === 'membership_deposit' ||
    (formData.payment_for === 'other' && !creatingNewCharge);

  const depositMembershipPayable =
    depositStatus && depositStatus.status === 'pending_payment' && Number(depositStatus.due_amount) > 0;

  const depositNoMembership = depositStatusFetched && !depositStatusLoading && !depositStatusError && !depositStatus;

  const depositBlocked =
    depositStatusFetched && !depositStatusLoading && !depositStatusError && depositStatus && !depositMembershipPayable;

  // Membership Deposit can only be submitted once a payable membership has
  // been resolved from the backend — everything else (still loading, no
  // membership, active/fully-paid) must block Save entirely.
  const membershipDepositReady = formData.payment_for !== 'membership_deposit' || depositMembershipPayable;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (saving) return;

    if (!formData.member_id) {
      Swal.fire('Error', 'Member is required', 'error');
      return;
    }

    if (formData.payment_for === 'membership_deposit' && !membershipDepositReady) {
      Swal.fire('Error', 'This member has no payable membership deposit right now', 'error');
      return;
    }

    if (formData.payment_for === 'fine' && !formData.fine_id) {
      Swal.fire('Error', 'Select the fine this payment is for', 'error');
      return;
    }

    if (formData.payment_for === 'other') {
      if (creatingNewCharge) {
        if (!formData.new_charge_category || !formData.new_charge_description || !formData.new_charge_amount) {
          Swal.fire('Error', 'Charge category, description and amount are required', 'error');
          return;
        }
      } else if (!formData.charge_id) {
        Swal.fire('Error', 'Select a charge, or create a new one', 'error');
        return;
      }
    }

    if (!formData.amount || Number(formData.amount) <= 0) {
      Swal.fire('Error', 'Amount is required', 'error');
      return;
    }

    try {
      setSaving(true);

      let chargeId = formData.charge_id;

      if (formData.payment_for === 'other' && creatingNewCharge) {
        const chargeResult = await addMemberCharge({
          member_id: formData.member_id,
          charge_category: formData.new_charge_category,
          description: formData.new_charge_description,
          amount: formData.new_charge_amount
        });

        chargeId = chargeResult.insertId;
      }

      if (formData.payment_mode === 'hdfc') {
        let session;

        if (formData.payment_for === 'membership_deposit') {
          session = await initiateMembershipPayment(formData.membership_id);
        } else if (formData.payment_for === 'fine') {
          session = await initiateFinePayment(formData.fine_id);
        } else {
          session = await initiateChargePayment(chargeId);
        }

        if (session.redirect_url) {
          window.location.href = session.redirect_url;
          return;
        }

        Swal.fire(
          'Payment session created',
          `Order ${session.internal_order_id} was created but no checkout link was returned.`,
          'info'
        );
        closeModal();
        return;
      }

      // library_id and received_by_staff_id are derived server-side from the
      // staff member's own token — the backend ignores/rejects them here.
      const payload = {
        member_id: formData.member_id,
        membership_id: formData.payment_for === 'membership_deposit' ? formData.membership_id : null,
        fine_id: formData.payment_for === 'fine' ? formData.fine_id : null,
        charge_id: formData.payment_for === 'other' ? chargeId : null,
        payment_for: formData.payment_for,
        amount: formData.amount,
        payment_method: formData.payment_mode,
        transaction_id: formData.transaction_ref || null,
        remarks: formData.remarks || null
      };

      await addPayment(payload);
      Swal.fire('Success', 'Payment added successfully', 'success');
      closeModal();
      fetchData();
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const filteredPayments = useMemo(() => {
    const keyword = search.toLowerCase();
    if (!keyword) return payments;

    return payments.filter((payment) => {
      return (
        String(payment.member_name || payment.parent_name || '').toLowerCase().includes(keyword) ||
        String(payment.payment_type || '').toLowerCase().includes(keyword) ||
        String(payment.payment_mode || '').toLowerCase().includes(keyword) ||
        String(payment.transaction_ref || '').toLowerCase().includes(keyword) ||
        String(payment.gateway_txn_id || '').toLowerCase().includes(keyword) ||
        String(payment.gateway_order_id || '').toLowerCase().includes(keyword) ||
        String(payment.payment_status || '').toLowerCase().includes(keyword)
      );
    });
  }, [payments, search]);

  const totalPages = Math.max(1, Math.ceil(filteredPayments.length / PAGE_SIZE));
  const paginatedPayments = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredPayments.slice(start, start + PAGE_SIZE);
  }, [filteredPayments, page]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const paymentExportColumns = [
    { header: 'Member', key: '__member' },
    { header: 'Type', key: 'payment_type' },
    { header: 'Amount', key: 'amount' },
    { header: 'Mode', key: 'payment_mode' },
    { header: 'Ref', key: '__ref' },
    { header: 'Status', key: 'payment_status' },
    { header: 'Date', key: '__date' }
  ];

  const paymentExportRows = filteredPayments.map((p) => ({
    ...p,
    __member: p.member_name || p.parent_name || '-',
    __ref: p.transaction_ref || p.gateway_txn_id || p.gateway_order_id || '-',
    __date: p.created_at ? String(p.created_at).slice(0, 10) : '-'
  }));

  const handleExportPaymentsCSV = () => exportToCSV(paymentExportColumns, paymentExportRows, 'payments_list');

  const getStatusBadge = (status) => {
    if (status === 'success') return 'bg-success';
    if (status === 'pending') return 'bg-warning text-dark';
    if (status === 'failed') return 'bg-danger';
    return 'bg-secondary';
  };

  // Online (HDFC) collections come in via the gateway; over-the-counter ones are
  // entered manually. Highlight online so staff can tell them apart at a glance.
  const isOnlinePayment = (payment) =>
    String(payment.payment_mode || '').toLowerCase() === 'online' ||
    !!payment.gateway || !!payment.gateway_order_id;

  // Manual payments store a transaction_ref; gateway payments store the HDFC
  // order / txn id instead. Fall back so online rows still show a reference.
  const getPaymentRef = (payment) =>
    payment.transaction_ref ||
    payment.gateway_txn_id ||
    payment.gateway_order_id ||
    '-';

  // How much of this online payment is still refundable.
  const refundableAmount = (payment) =>
    Number(payment.amount || 0) - Number(payment.refunded_amount || 0);

  // A gateway payment can be refunded only if it succeeded and has balance left.
  const canRefund = (payment) =>
    isOnlinePayment(payment) &&
    String(payment.payment_status || '').toLowerCase() === 'success' &&
    refundableAmount(payment) > 0;

  const handleRefund = async (payment) => {
    const maxRefund = refundableAmount(payment);

    const { value: amount, isConfirmed } = await Swal.fire({
      title: 'Refund Payment',
      html: `<div style="text-align:left;font-size:13px;color:#475569;margin-bottom:8px;">
        ${payment.member_name || payment.parent_name || 'Member'}<br/>
        Refundable: <b>₹ ${maxRefund}</b>
      </div>`,
      input: 'number',
      inputLabel: 'Refund amount',
      inputValue: maxRefund,
      inputAttributes: { min: 1, step: '0.01' },
      showCancelButton: true,
      confirmButtonText: 'Refund',
      inputValidator: (value) => {
        if (value === '' || value === null) return 'Please enter an amount';
        if (Number(value) <= 0) return 'Amount must be greater than 0';
        if (Number(value) > maxRefund) return `Amount cannot exceed ₹ ${maxRefund}`;
        return null;
      }
    });

    if (!isConfirmed) return;

    try {
      setSaving(true);
      await refundPayment(payment.id, Number(amount), 'fine_refund');
      Swal.fire('Requested', 'Refund initiated. It will confirm shortly.', 'success');
      fetchData();
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  // `member` is carried through onto the normalized option so renderOption
  // below can show mobile/membership no./type/status per row — the closed
  // control still only ever shows the plain `label`.
  const memberOptions = memberSearchResults.map((member) => ({
    value: member.id,
    label: member.membership_no ? `${memberLabel(member)} (${member.membership_no})` : memberLabel(member),
    member
  }));

  const renderMemberOption = (option) => {
    const member = option.member || {};
    const type = member.membership_type || member.member_type;
    const status = member.membership_status;

    return (
      <div className="pm-member-option">
        <div className="pm-member-option__top">
          <span className="pm-member-option__name">{memberDisplayName(member)}</span>
          {member.membership_no && <span className="pm-member-option__no">{member.membership_no}</span>}
        </div>
        <div className="pm-member-option__meta">
          {member.mobile && (
            <span className="pm-member-option__mobile"><i className="fas fa-phone-alt" />{member.mobile}</span>
          )}
          {type && <span className="pm-member-option__type">{formatEnumLabel(type)}</span>}
          {status && (
            <span className={`badge ${MEMBERSHIP_STATUS_BADGE_CLASS[status] || 'bg-secondary'} pm-member-option__status`}>
              {formatEnumLabel(status)}
            </span>
          )}
        </div>
      </div>
    );
  };

  const memberNoOptionsMessage = memberSearchError
    ? `Search failed: ${memberSearchError}`
    : memberSearchTerm.trim()
      ? `No members match "${memberSearchTerm}"`
      : 'No members found';

  return (
    <div className="container-fluid mt-3">
      {!embedded && (
        <div className="lib-page-header">
          <div className="lib-page-header__left">
            <div className="lib-page-header__icon">
              <i className="fas fa-wallet" />
            </div>
            <div>
              <h3 className="lib-page-header__title">Payments</h3>
              <div className="lib-page-header__subtitle">Collect fine, deposit or membership payments</div>
            </div>
          </div>
          <div className="lib-page-header__actions">
            <button className="btn btn-primary" onClick={openModal} disabled={loading}>
              <i className="fas fa-money-check-alt me-2" />Create Payment
            </button>
          </div>
        </div>
      )}

      {embedded && actionsPortalNode && createPortal(
        <button className="btn btn-primary" onClick={openModal} disabled={loading}>
          <i className="fas fa-money-check-alt me-2" />Create Payment
        </button>,
        actionsPortalNode
      )}

      {showModal && (
        <div className="pm-modal-overlay">
          <div className="pm-modal-box">
            <form onSubmit={handleSubmit}>
              <div className="pm-modal-head">
                <h5><i className="fas fa-money-check-alt me-2" />Create Payment</h5>
                <button type="button" className="pm-modal-close" onClick={closeModal} disabled={saving}>
                  ×
                </button>
              </div>

              <div className="pm-modal-body">
                <div className="row">
                  <div className="col-md-4 mb-3 pm-combobox">
                    <label className="form-label">Member *</label>
                    <SearchableSelect
                      name="member_id"
                      value={formData.member_id}
                      onChange={handleMemberSearchableChange}
                      onSearchChange={handleMemberSearchChange}
                      options={memberOptions}
                      renderOption={renderMemberOption}
                      loading={memberSearchLoading}
                      loadingMessage="Loading members…"
                      placeholder="Search name, mobile or membership no."
                      noOptionsMessage={memberNoOptionsMessage}
                      panelClassName="pm-member-panel"
                      required
                    />

                    {!formData.member_id && memberSearchTerm && (
                      <div className="small text-danger">Select a member from the list</div>
                    )}
                  </div>

                  <div className="col-md-4 mb-3">
                    <label className="form-label">Payment For</label>
                    <SearchableSelect
                      name="payment_for"
                      value={formData.payment_for}
                      onChange={handlePaymentForChange}
                      options={PAYMENT_FOR_OPTIONS}
                      clearable={false}
                    />
                  </div>

                  {formData.payment_for === 'fine' && (
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Fine *</label>
                      <SearchableSelect
                        name="fine_id"
                        value={formData.fine_id}
                        onChange={handleFineChange}
                        options={memberFines.map((fine) => ({
                          value: fine.id,
                          label: `${fine.fine_type} - ₹${fine.balance_amount ?? fine.amount}`
                        }))}
                        loading={loadingMemberData}
                        loadingMessage="Loading fines…"
                        noOptionsMessage="No eligible fines found"
                        placeholder="Select Fine"
                        disabled={loadingMemberData}
                        required
                      />
                      {!loadingMemberData && formData.member_id && memberFines.length === 0 && (
                        <div className="small text-muted">No eligible fines found for this member.</div>
                      )}
                    </div>
                  )}

                  {formData.payment_for === 'membership_deposit' && (
                    <div className="col-md-8 mb-3">
                      <label className="form-label">Membership</label>
                      {!formData.member_id ? (
                        <div className="small text-muted">Select a member first</div>
                      ) : depositStatusLoading ? (
                        <div className="small text-muted">Loading membership...</div>
                      ) : depositStatusError ? (
                        <div className="small text-danger">{depositStatusError}</div>
                      ) : depositNoMembership ? (
                        <div className="small text-danger">No membership record found for this member.</div>
                      ) : depositMembershipPayable ? (
                        <div className="pm-membership-summary">
                          <div>
                            <strong>{depositStatus.membership_no}</strong> ({depositStatus.status})
                          </div>
                          <div className="small text-muted">
                            Required: ₹{depositStatus.required_amount} · Paid: ₹{depositStatus.paid_amount} · Due: ₹{depositStatus.due_amount}
                          </div>
                        </div>
                      ) : depositBlocked ? (
                        <div>
                          <div className="small text-danger">Membership already active — no deposit payment is due.</div>
                          <div className="small text-muted">{depositStatus.membership_no} — {depositStatus.status}</div>
                        </div>
                      ) : null}
                    </div>
                  )}

                  {formData.payment_for === 'other' && (
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Charge *</label>
                      <SearchableSelect
                        name="charge_id"
                        value={creatingNewCharge ? '__new__' : formData.charge_id}
                        onChange={handleChargeChange}
                        options={[
                          ...memberCharges.map((charge) => ({
                            value: charge.id,
                            label: `${charge.charge_category} - ${charge.description} - ₹${charge.amount}`
                          })),
                          { value: '__new__', label: '+ Create New Charge' }
                        ]}
                        loading={loadingMemberData}
                        placeholder="Select Charge"
                        disabled={loadingMemberData}
                        required
                      />
                    </div>
                  )}

                  {formData.payment_for === 'other' && creatingNewCharge && (
                    <>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">Charge Category *</label>
                        <input
                          type="text"
                          name="new_charge_category"
                          className="form-control"
                          value={formData.new_charge_category}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">Description *</label>
                        <input
                          type="text"
                          name="new_charge_description"
                          className="form-control"
                          value={formData.new_charge_description}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">Charge Amount *</label>
                        <input
                          type="number"
                          name="new_charge_amount"
                          className="form-control"
                          value={formData.new_charge_amount}
                          onChange={handleNewChargeAmountChange}
                          min="0.01"
                          step="0.01"
                          required
                        />
                      </div>
                    </>
                  )}

                  <div className="col-md-4 mb-3">
                    <label className="form-label">Amount *</label>
                    <input
                      type="number"
                      name="amount"
                      className="form-control"
                      value={formData.amount}
                      onChange={handleChange}
                      readOnly={isAmountLocked}
                      disabled={formData.payment_for === 'membership_deposit' && !membershipDepositReady}
                      required
                    />
                    {isAmountLocked && (
                      <div className="small text-muted">Server-calculated — not editable</div>
                    )}
                  </div>

                  <div className="col-md-4 mb-3">
                    <label className="form-label">Mode</label>
                    <SearchableSelect
                      name="payment_mode"
                      value={formData.payment_mode}
                      onChange={handlePaymentModeChange}
                      options={PAYMENT_MODE_OPTIONS}
                      clearable={false}
                    />
                  </div>

                  <div className="col-md-4 mb-3">
                    <label className="form-label">Transaction Ref</label>
                    <input
                      type="text"
                      name="transaction_ref"
                      className="form-control"
                      value={formData.transaction_ref}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-12 mb-3">
                    <label className="form-label">Remarks</label>
                    <input
                      type="text"
                      name="remarks"
                      className="form-control"
                      value={formData.remarks}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              <div className="pm-modal-footer">
                <button type="button" className="btn btn-light" onClick={closeModal} disabled={saving}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving || (formData.payment_for === 'membership_deposit' && !membershipDepositReady)}
                >
                  {saving
                    ? 'Saving...'
                    : formData.payment_mode === 'hdfc'
                      ? 'Proceed to HDFC Checkout'
                      : 'Save Payment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card shadow-sm mb-3 table-full-card">
        <div className="card-header fw-bold d-flex justify-content-between align-items-center">
          <span><i className="fas fa-list me-2" />Payment List</span>
          <span>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleExportPaymentsCSV}>CSV</button>
          </span>
        </div>

        <div className="card-body">
          <div className="row g-2 align-items-center mb-3">
            <div className="col-md-4">
              <input
                type="text"
                className="form-control"
                placeholder="Search member, type, mode, ref..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <button type="button" className="btn btn-outline-secondary w-100" onClick={() => setSearch('')}>
                Reset
              </button>
            </div>
          </div>

          <div className="table-responsive pm-table-wrap">
            <table className="table table-bordered table-hover align-middle lib-table-cell">
              <thead className="table-light lib-table-header">
                <tr>
                  <th>#</th>
                  <th>Member</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Mode</th>
                  <th>Ref</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Remarks</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {paginatedPayments.length > 0 ? (
                  paginatedPayments.map((payment, index) => (
                    <tr key={payment.id}>
                      <td>{(page - 1) * PAGE_SIZE + index + 1}</td>
                      <td data-label="Member">
                        <strong>{payment.member_name || payment.parent_name || '-'}</strong>
                        <div className="small text-muted">{payment.mobile || '-'}</div>
                      </td>
                      <td data-label="Type">{payment.payment_type || '-'}</td>
                      <td data-label="Amount">₹ {payment.amount || 0}</td>
                      <td data-label="Mode">
                        {isOnlinePayment(payment) ? (
                          <span className="badge bg-primary lib-status-badge">
                            <i className="fas fa-globe me-1" />
                            {payment.payment_mode || 'online'}
                          </span>
                        ) : (
                          payment.payment_mode || '-'
                        )}
                      </td>
                      <td data-label="Ref" className="lib-ref-text">{getPaymentRef(payment)}</td>
                      <td data-label="Status">
                        <span className={`badge ${getStatusBadge(payment.payment_status)} lib-status-badge`}>
                          {payment.payment_status || '-'}
                        </span>
                      </td>
                      <td data-label="Date">
                        {payment.created_at ? String(payment.created_at).slice(0, 10) : '-'}
                      </td>
                      <td data-label="Remarks">{payment.remarks || '-'}</td>
                      <td data-label="Action">
                        {canRefund(payment) ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger lib-btn-text"
                            onClick={() => handleRefund(payment)}
                            disabled={saving}
                          >
                            <i className="fas fa-undo me-1" />Refund
                          </button>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="text-center text-muted py-4">
                      No payments found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <Pagination
              page={page}
              totalPages={totalPages}
              totalItems={filteredPayments.length}
              itemLabel="payments"
              onPageChange={setPage}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentList;
